import { type FC, useState, useMemo } from 'react';
import PairFrequencyHeatmap from './PairFrequencyHeatmap';
import TripleFrequencyChart from './TripleFrequencyChart';
import FilterBadge, { EmptyFilterState } from './FilterBadge';
import { useStatisticsData } from '@/pages/statistics/StatisticsDataContext';

const PairAnalysis: FC = () => {
  const { filteredDraws, loading, advancedFilters, resetFilters } = useStatisticsData();
  const draws = filteredDraws;
  const [activeTab, setActiveTab] = useState<'pair' | 'triple' | 'oddEven' | 'range' | 'consecutive'>('pair');

  const filterKey = useMemo(() => {
    return `${advancedFilters.numberRanges.join(',')}|${advancedFilters.dayOfWeeks.join(',')}|${advancedFilters.prizeRange}`;
  }, [advancedFilters]);

  const oddEvenDist = useMemo(() => {
    if (!draws.length) return [];
    const dist: Record<string, number> = {
      '6:0': 0, '5:1': 0, '4:2': 0, '3:3': 0, '2:4': 0, '1:5': 0, '0:6': 0,
    };
    draws.forEach((draw) => {
      const oddCount = draw.numbers.filter((n) => n % 2 === 1).length;
      const evenCount = 6 - oddCount;
      const key = `${oddCount}:${evenCount}`;
      dist[key] = (dist[key] || 0) + 1;
    });
    return Object.entries(dist).map(([label, count]) => ({
      label,
      count,
      percentage: Math.round((count / draws.length) * 100),
    }));
  }, [draws]);

  const rangeDist = useMemo(() => {
    if (!draws.length) return [];
    const ranges = [
      { label: '1~10', min: 1, max: 10 },
      { label: '11~20', min: 11, max: 20 },
      { label: '21~30', min: 21, max: 30 },
      { label: '31~40', min: 31, max: 40 },
      { label: '41~45', min: 41, max: 45 },
    ];
    return ranges.map((r) => {
      let total = 0;
      draws.forEach((draw) => {
        draw.numbers.forEach((n) => {
          if (n >= r.min && n <= r.max) total++;
        });
      });
      return {
        ...r,
        count: total,
        percentage: Math.round((total / (draws.length * 6)) * 100),
      };
    });
  }, [draws]);

  const consecutiveInfo = useMemo(() => {
    if (!draws.length) return { withConsecutive: 0, withoutConsecutive: 0, total: 0 };
    let withCon = 0;
    draws.forEach((draw) => {
      const sorted = [...draw.numbers].sort((a, b) => a - b);
      let hasConsecutive = false;
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] - sorted[i - 1] === 1) {
          hasConsecutive = true;
          break;
        }
      }
      if (hasConsecutive) withCon++;
    });
    return {
      withConsecutive: withCon,
      withoutConsecutive: draws.length - withCon,
      total: draws.length,
    };
  }, [draws]);

  if (loading) {
    return (
      <div className="bg-background-100/80 border border-background-200/60 rounded-2xl p-6 animate-pulse">
        <div className="h-5 bg-background-200 rounded w-32 mb-4"></div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-10 bg-background-200 rounded-lg"></div>
          ))}
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'pair' as const, label: '번호 쌍 TOP 30', icon: 'ri-link' },
    { key: 'triple' as const, label: '번호 삼중 TOP 30', icon: 'ri-stack-line' },
    { key: 'oddEven' as const, label: '홀짝 비율', icon: 'ri-contrast-2-line' },
    { key: 'range' as const, label: '번호대 분포', icon: 'ri-bar-chart-horizontal-line' },
    { key: 'consecutive' as const, label: '연속번호', icon: 'ri-arrow-left-right-line' },
  ];

  const isEmpty = draws.length === 0;

  return (
    <div className="bg-background-100/80 border border-background-200/60 rounded-2xl p-6">
      <div className="flex items-center gap-2.5 mb-5 flex-wrap">
        <div className="w-8 h-8 rounded-lg bg-secondary-500 flex items-center justify-center">
          <i className="ri-pie-chart-line text-white text-sm"></i>
        </div>
        <h3 className="font-heading text-lg text-foreground-900">심층 분석</h3>
        <FilterBadge advancedFilters={advancedFilters} onReset={resetFilters} />
      </div>

      {/* Sub tabs */}
      <div className="flex gap-1 mb-5 bg-background-100 rounded-full p-1 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
              activeTab === tab.key
                ? 'bg-background-50 text-foreground-900 shadow-sm'
                : 'text-foreground-400 hover:text-foreground-600'
            }`}
          >
            <i className={tab.icon}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content with filter transition */}
      <div key={`${filterKey}-${activeTab}`} className="animate-[contentFadeIn_0.35s_ease-out]">
        {isEmpty ? (
          <EmptyFilterState />
        ) : (
          <>
            {/* Pair frequency — heatmap */}
            {activeTab === 'pair' && <PairFrequencyHeatmap />}

            {/* Triple frequency — bar chart */}
            {activeTab === 'triple' && <TripleFrequencyChart />}

            {/* Odd/Even distribution */}
            {activeTab === 'oddEven' && (
              <div>
                <p className="text-xs text-foreground-500 mb-4">
                  당첨번호의 홀수:짝수 비율 분포입니다. 전체 {draws.length}회차 기준입니다.
                </p>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {oddEvenDist.map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <span className="text-xs font-mono font-bold text-foreground-600 w-10 flex-shrink-0">
                        {item.label}
                      </span>
                      <div className="flex-1 h-7 bg-background-50 rounded-md overflow-hidden relative">
                        <div
                          className={`h-full rounded-md transition-all duration-500 ${
                            item.percentage >= 40 ? 'bg-secondary-500' :
                            item.percentage >= 25 ? 'bg-secondary-400' :
                            item.percentage >= 15 ? 'bg-secondary-300' :
                            'bg-secondary-200'
                          }`}
                          style={{ width: `${Math.max(item.percentage, 2)}%` }}
                        />
                        <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-xs font-bold text-foreground-700">
                          {item.count}회
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400 w-10 text-right flex-shrink-0">{item.percentage}%</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 rounded-lg bg-secondary-50/60 border border-secondary-200/40">
                  <p className="text-xs text-secondary-700">
                    <i className="ri-lightbulb-line mr-1"></i>
                    실제 로또 추첨에서 가장 많이 나오는 비율은 <strong>4:2</strong>와 <strong>3:3</strong>입니다.
                    번호를 선택할 때 홀짝 균형을 고려해보세요!
                  </p>
                </div>
              </div>
            )}

            {/* Range distribution */}
            {activeTab === 'range' && (
              <div>
                <p className="text-xs text-foreground-500 mb-4">
                  번호대별(10단위) 출현 분포입니다. 전체 {draws.length * 6}개의 당첨번호 기준입니다.
                </p>
                <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
                  {rangeDist.map((item) => (
                    <div key={item.label}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-bold text-foreground-700">
                          {item.label}번대
                        </span>
                        <span className="text-xs text-foreground-400">
                          {item.count}회 ({item.percentage}%)
                        </span>
                      </div>
                      <div className="h-6 bg-background-50 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            item.label === '1~10' ? 'bg-secondary-500' :
                            item.label === '11~20' ? 'bg-secondary-400' :
                            item.label === '21~30' ? 'bg-secondary-300' :
                            item.label === '31~40' ? 'bg-secondary-200' :
                            'bg-secondary-100'
                          }`}
                          style={{ width: `${Math.max(item.percentage, 2)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 rounded-lg bg-secondary-50/60 border border-secondary-200/40">
                  <p className="text-xs text-secondary-700">
                    <i className="ri-lightbulb-line mr-1"></i>
                    모든 번호대에서 골고루 선택하는 것이 통계적으로 가장 일반적인 패턴이에요.
                    특정 번호대에 치우치지 않도록 분산해서 골라보세요.
                  </p>
                </div>
              </div>
            )}

            {/* Consecutive numbers */}
            {activeTab === 'consecutive' && (
              <div>
                <p className="text-xs text-foreground-500 mb-4">
                  연속된 번호(예: 12, 13)가 포함된 회차와 그렇지 않은 회차의 비율입니다.
                </p>
                <div className="flex items-center justify-center gap-4 md:gap-8 py-6 md:py-8">
                  <div className="text-center">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-secondary-100 flex items-center justify-center mb-3 mx-auto">
                      <span className="text-xl md:text-2xl font-bold text-secondary-700">
                        {consecutiveInfo.total > 0 ? Math.round((consecutiveInfo.withConsecutive / consecutiveInfo.total) * 100) : 0}%
                      </span>
                    </div>
                    <p className="text-sm text-foreground-700 font-medium">연속번호 있음</p>
                    <p className="text-xs text-foreground-400">{consecutiveInfo.withConsecutive}회</p>
                  </div>
                  <div className="text-center">
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-background-200 flex items-center justify-center mb-3 mx-auto">
                      <span className="text-xl md:text-2xl font-bold text-foreground-600">
                        {consecutiveInfo.total > 0 ? Math.round((consecutiveInfo.withoutConsecutive / consecutiveInfo.total) * 100) : 0}%
                      </span>
                    </div>
                    <p className="text-sm text-foreground-700 font-medium">연속번호 없음</p>
                    <p className="text-xs text-foreground-400">{consecutiveInfo.withoutConsecutive}회</p>
                  </div>
                </div>
                <div className="mt-2 p-3 rounded-lg bg-secondary-50/60 border border-secondary-200/40">
                  <p className="text-xs text-secondary-700">
                    <i className="ri-information-line mr-1"></i>
                    과거 데이터를 보면 약 절반의 회차에서 연속된 번호가 1쌍 이상 포함되어 있습니다.
                    연속번호 1~2쌍은 자연스러운 패턴이에요!
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PairAnalysis;